import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, BedDouble, ArrowUpRight, Images, MapPin } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow, EASE } from "./motion";
import { HouseGallery } from "./HouseGallery";
import { useLang, roomsLabel } from "../i18n";

const CORACAO = Array.from({ length: 22 }, (_, k) => `/img/casas/coracao2/${k + 1}.jpg`);
const MARIAS = Array.from({ length: 20 }, (_, k) => `/img/casas/marias2/${k + 1}.jpg`);
const HORTA = Array.from({ length: 23 }, (_, k) => `/img/casas/horta2/${k + 1}.jpg`);

export const HOUSES = [
  { id: "marias", booking: "https://www.booking.com/hotel/pt/casa-das-marias-reguengos-de-monsaraz.pt-pt.html?aid=356980&label=mkt123sc-deb6a180-80a2-49dc-abd3-b41ae2d0aed6&sid=6775408730465dbc19147ea46927679b&dest_id=-2174259&dest_type=city&dist=0&group_adults=2&group_children=0&hapos=1&hpos=1&no_rooms=1&req_adults=2&req_children=0&room1=A%2CA&sb_price_type=total&sr_order=popularity&srepoch=1792594075&srpvid=5af3680abefa0775&type=total&ucfs=1&", name: "Casa Das Maria's", type: "T2", roomsN: 2, guests: 4, img: MARIAS[0], video: "/video/marias", photos: MARIAS, address: "Beco da Horta, 1 · Reguengos de Monsaraz", hours: "Check-in 15:30 · Check-out 11:00" },
  { id: "horta", booking: "https://www.booking.com/hotel/pt/casa-recanto-da-horta.pt-pt.html?aid=356980&label=mkt123sc-deb6a180-80a2-49dc-abd3-b41ae2d0aed6&sid=6775408730465dbc19147ea46927679b&dest_id=-2174259&dest_type=city&dist=0&group_adults=2&group_children=0&hapos=8&hpos=8&no_rooms=1&req_adults=2&req_children=0&room1=A%2CA&sb_price_type=total&sr_order=popularity&srepoch=1792594404&srpvid=5af3680abefa0775&type=total&ucfs=1&", name: "Casa Recanto da Horta", type: "T2", roomsN: 2, guests: 6, img: HORTA[0], video: "/video/horta", photos: HORTA, address: "Beco da Horta, 3 · Reguengos de Monsaraz", hours: "Check-in 15:30 · Check-out 11:00" },
  { id: "coracao", booking: "https://www.booking.com/hotel/pt/casa-coracao-alentejano.pt-pt.html?aid=318615&label=New_English_EN_PT_26767228345-fV4MdZaRH5BXvGAf__NIKgS813057949660%3Apl%3Ata%3Ap1%3Ap2%3Aac%3Aap%3Aneg%3Afi%3Atikwl-166032399385%3Alp20870%3Ali%3Adec%3Adm%3Aag26767228345%3Acmp394644265&sid=6775408730465dbc19147ea46927679b&dest_id=-2174259&dest_type=city&dist=0&group_adults=2&group_children=0&hapos=1&hpos=1&no_rooms=1&req_adults=2&req_children=0&room1=A%2CA&sb_price_type=total&sr_order=popularity&srepoch=1792593473&srpvid=e86a66dbd1ab0019&type=total&ucfs=1&", name: "Casa Coração Alentejano", type: "T1", roomsN: 1, guests: 3, img: CORACAO[0], video: "/video/coracao", photos: CORACAO, address: "Rua da Travessa, 18 · Reguengos de Monsaraz", hours: "Check-in 15:30 · Check-out 11:00" },
];

const HouseCard = ({ h, i, onOpen }) => {
  const { t } = useLang();
  const tx = t.houses;
  return (
  <motion.article
    data-testid={`house-card-${h.id}`}
    initial={{ opacity: 0, y: 80 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1.1, ease: EASE, delay: i * 0.12 }}
    className={`group ${i === 1 ? "md:mt-24" : ""}`}
  >
    <div className="relative arch overflow-hidden aspect-[3/4] bg-[#D8C7B5]">
      {h.video ? (
        <video data-testid={`house-video-${h.id}`} poster={h.img} autoPlay muted loop playsInline preload="metadata" className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110">
          <source src={`${h.video}.webm`} type="video/webm" />
          <source src={`${h.video}.mp4`} type="video/mp4" />
        </video>
      ) : (
        <img src={h.img} alt={h.name} className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#1C1D1A]/60 via-transparent to-transparent opacity-80" />
      <span className="absolute top-6 left-1/2 -translate-x-1/2 eyebrow text-[10px] bg-[#F7F5F0]/90 backdrop-blur px-4 py-2 rounded-full text-[#1C1D1A]">{tx[h.id].tag}</span>
      <span className="absolute bottom-5 left-6 font-display text-7xl text-[#F7F5F0]/90 leading-none">{h.type}</span>
      {h.photos && (
        <button
          data-testid={`house-gallery-open-${h.id}`}
          onClick={() => onOpen(h)}
          className="absolute bottom-6 right-6 btn-pill text-xs bg-[#F7F5F0]/90 backdrop-blur text-[#1C1D1A] hover:bg-[#F7F5F0]"
        >
          <Images className="w-4 h-4" /> {tx.photos} ({h.photos.length})
        </button>
      )}
    </div>
    <div className="pt-6">
      <h3 data-testid={`house-name-${h.id}`} className="font-display text-3xl text-[#1C1D1A] leading-tight">{h.name}</h3>
      <span data-testid={`house-price-${h.id}`} className="block mt-1 text-xs text-[#8C6A55] font-mono">{tx.price}</span>
      <p className="text-sm text-[#5C5E56] mt-2 leading-relaxed">{tx[h.id].text}</p>
      {h.address && (
        <p data-testid={`house-address-${h.id}`} className="flex items-start gap-1.5 text-xs text-[#5C5E56] mt-3"><MapPin className="w-3.5 h-3.5 mt-px shrink-0 text-[#B85D3B]" />{h.address}<br />{h.hours}</p>
      )}
      <div className="flex flex-wrap gap-5 mt-5 text-xs text-[#4A4C45] font-mono uppercase tracking-wider">
        <span className="flex items-center gap-1.5"><BedDouble className="w-3.5 h-3.5" />{roomsLabel(h.roomsN, t)}</span>
        <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{h.guests} {tx.guests}</span>
      </div>
      <a
        data-testid={`house-book-now-button-${h.id}`}
        href={h.booking}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 w-full btn-pill justify-between border border-[#1C1D1A] text-[#1C1D1A] hover:bg-[#1C1D1A] hover:text-[#F7F5F0]"
      >
        {tx.book} <ArrowUpRight className="w-4 h-4" />
      </a>
    </div>
  </motion.article>
  );
};

export const Houses = () => {
  const [open, setOpen] = useState(null);
  const { t } = useLang();
  const tx = t.houses;
  return (
  <section id="houses" data-testid="houses-section" className="bg-[#EFECE6] py-24 md:py-40">
    <div className="max-w-[1440px] mx-auto px-5 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
        <div>
          <Eyebrow>{tx.eyebrow}</Eyebrow>
          <MaskedLines lines={[tx.l1, <em key="e" className="italic text-[#4A5D4E]">{tx.l2}</em>]} className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6 text-[#1C1D1A]" />
        </div>
        <Reveal className="max-w-sm">
          <p className="text-[#5C5E56] leading-relaxed">{tx.intro}</p>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-3 gap-14 md:gap-8 lg:gap-12">
        {HOUSES.map((h, i) => <HouseCard key={h.id} h={h} i={i} onOpen={setOpen} />)}
      </div>
    </div>
    <AnimatePresence>{open && <HouseGallery house={open} onClose={() => setOpen(null)} />}</AnimatePresence>
  </section>
  );
};
